###
Drag and Drop is still a mess; here we attempt to provide a consistent way to
get the types of items that are being dragged and the types and content of
items that are dropped

State of Drag and Drop for supported browsers:

Chrome

- Things you can drag: Files, Urls, Text
- How you get the types of things being dragged: e.dataTransfer.item[].type
- How you get the content that was dropped: e.dataTransfer.item.getAsString or
  e.dataTransfer.getData()

Firefox

- Things you can drag: Files, Urls, Text
- How you get the types of things being dragged: e.dataTransfer.types
- How you get the content that was dropped: e.dataTransfer.getData()

Internet Explorer / Edge

- Things you can drag: Files, Urls, Text
  (Text may not work in some cases unless you've enabled "Allow dragging of
  content between domains into separate windows")
- How you get the types of things being dragged: e.dataTransfer.types
- How you get the content that was dropped: e.dataTransfer.getData()

Safari

- Things you can drag: Files, Urls
- How you get the types of things being dragged: e.dataTransfer.types, although
  there isn't a good way to detect this
- How you get the content that was dropped: e.dataTransfer.getData()
###

Promise = require 'bluebird'
_ = require 'underscore'
isUrl = require 'app/scripts/lib/util/url/is-url'

# Chrome has dataTransfer.items; you can figure out the type by looking at
# the item.kind and item.type
getTypeFromItem = (item) ->
  knownType = switch item.kind
    when 'file'
      'files'
    when 'string'
      switch item.type
        when 'text/uri-list' then 'url'
        when 'text/plain' then 'text'

  knownType || 'unknown'

# Chrome has dataTransfer.items
getTypesUsingItems = (dataTransfer) ->
  _.chain(dataTransfer.items)
  .map(getTypeFromItem)
  .compact()
  .uniq()
  .value()

# IE uses types like Files/Url/Text
# Firefox uses things that look like mime types, and also the IE ones
# Safari includes text/uri-list for links
getTypeFromType = (type) ->
  switch type
    when 'Files', 'application/x-moz-file' then 'files'
    when 'Url', 'text/x-moz-url', 'text/uri-list' then 'url'
    when 'Text', 'text/plain' then 'text'
    else 'unknown'

# Map the browser types to our simple types (files, url, text)
getTypesUsingTypes = (dataTransfer) ->
  _.chain(dataTransfer.types)
  .map(getTypeFromType)
  .compact()
  .uniq()
  .value()

# If dataTransfer.files isn't empty, we're dragging files
getTypesUsingFiles = (dataTransfer) ->
  if dataTransfer.files.length > 0
    ['files']
  else
    []

# Chrome has dataTransfer.items
getDroppedUsingItems = (dataTransfer) ->
  # [MAPPING]
  Promise.all _.toArray(dataTransfer.items).map (item) ->
    type = getTypeFromItem(item)

    if type in ['text', 'url']
      new Promise (resolve) ->
        item.getAsString (content) ->
          resolve({ type, content })
    else if type == 'files'
      # getAsFile can actually take a while if the file is large (e.g. 20mb),
      # but we can't give any sort of message while we're thinking about it
      # because there can't be any async steps in the path between the "paste"
      # event and the getAsFile (I'm guessing it's a security precaution)
      { type, content: item.getAsFile() }
    else
      null

# All the browsers seem to have dataTransfer.files
getDroppedUsingFiles = (dataTransfer) ->
  _.map dataTransfer.files, (content) ->
    { type: 'files', content }

# Firefox, Safari and IE support getData
getDroppedUsingGetData = (dataTransfer) ->
  # Get the list of actual types, since that's what getData will expect
  types = dataTransfer.types
  # Unfortunately IE doesn't appear to set the types on clipboardData when
  # pasting text (types is just null)
  types ?= if dataTransfer.getData('Text') then ['Text'] else []

  _.map types, (type) ->
    simpleType = getTypeFromType(type)
    if simpleType in ['text', 'url'] && (content = dataTransfer.getData(type))
      type: simpleType
      content: content
    else
      null

# Given a DataTransfer (from a drop event or a paste event) return a normalized
# map of files/text/url
contentFromDataTransfer = (dataTransfer) ->
  # [MAPPING]
  Promise.all (droppedGetters.map (getter) -> getter(dataTransfer))
  .then (dropped) ->
    _.chain(dropped)
    .flatten()
    .compact()
    .each (entry) ->
      if entry.type == 'text' && isUrl(entry.content)
        entry.type = 'url'
      return
    .reject (entry) ->
      # e.g. file:// is considered a URL but we can't do anything with those
      entry.type == 'url' && !isUrl(entry.content)
    .groupBy('type')
    .mapObject (values, key) ->
      # For files we give an array of the File objects, for everything else
      # (text and urls) we assume there's only one thing
      if key == 'files'
        _.pluck(values, 'content')
      else
        values[0].content
    .value()

isMacSafari = ->
  /mac/i.test(navigator.platform) && /safari/i.test(navigator.userAgent)

# Attempt to do some basic feature detection by looking at what's defined on
# the DataTransfer prototype
hasSupport = (propertyName) ->
  # Older versions of safari (pre 7.1) used the Clipboard type for drag events
  DataTransferType = window.DataTransfer || window.Clipboard
  # We don't know what type is used for drag/drop events, so we can't do
  # feature detection
  if !DataTransferType
    return false

  DataTransferType.prototype.hasOwnProperty(propertyName) ||
  # There doesn't seem to be a way to see that Safai includes types on their
  # event.dataTransfer
  (propertyName == 'types' && isMacSafari()) ||
  # ... (and pre 7.1 safari doesn't indicate that Clipboard has files)
  (!window.DataTransfer && propertyName == 'files' && isMacSafari())

getTypes =
  if hasSupport('items')
    getTypesUsingItems
  else if hasSupport('types')
    getTypesUsingTypes
  else if hasSupport('files')
    getTypesUsingFiles
  else
    -> []

droppedGetters =
  if hasSupport('items')
    [getDroppedUsingItems]
  else
    _.compact([
      if hasSupport('files') then getDroppedUsingFiles
      if hasSupport('getData') and hasSupport('types') then getDroppedUsingGetData
    ])

getDropped = (event) ->
  contentFromDataTransfer(event.dataTransfer)

getPasted = (event) ->
  if event.clipboardData?
    contentFromDataTransfer(event.clipboardData)
  else if window.clipboardData?
    # Internet Explorer
    contentFromDataTransfer(window.clipboardData)
  else
    Promise.resolve({})

module.exports = {
  getTypes
  getDropped
  getPasted
}

# [MAPPING]
#
# We can't use Promise.map here, as it will resolve the body on the next tick.
# This is bad, because on the next tick the next tick the relevant properties
# of each item will be cleared out -- item.type, for example, will just give
# you the empty string.
